import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import { Toggle } from '../../../shared/components/Toggle';
import Button from '../../../shared/components/Button';
import { DepositRuleFields } from './DepositRuleFields';
import { CheckboxMultiSelect } from './CheckboxMultiSelect';
import { formatPrice } from './charterFormat';
import type { CharterPackage, CharterService, CreatePackagePayload, DepositRule } from '../types';

interface Props {
  initial?: CharterPackage | null;
  services: CharterService[];
  onSubmit: (values: CreatePackagePayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function PackageForm({ initial, services, onSubmit, onCancel, isSaving }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [serviceIds, setServiceIds] = useState<string[]>(initial?.serviceIds ?? []);
  const [priceMajor, setPriceMajor] = useState(initial && initial.price ? (initial.price / 100).toString() : '');
  const [depositRule, setDepositRule] = useState<DepositRule>(initial?.depositRule ?? { type: 'none' });
  const [requiresSlot, setRequiresSlot] = useState(initial?.requiresSlot ?? true);
  const [error, setError] = useState('');

  const options = services.map((s) => ({
    value: s.id,
    label: s.name,
    sublabel: `${s.durationMinutes} min · ${formatPrice(s.price, s.currency)}`,
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (serviceIds.length === 0) {
      setError('Select at least one service to bundle');
      return;
    }
    const priceNum = priceMajor.trim() === '' ? 0 : parseFloat(priceMajor);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setError('Price cannot be negative (0 = free)');
      return;
    }
    if (depositRule.type === 'percentage') {
      const pct = depositRule.percentageValue ?? 0;
      if (pct < 1 || pct > 100) {
        setError('Deposit percentage must be between 1 and 100');
        return;
      }
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      serviceIds,
      price: Math.round(priceNum * 100),
      depositRule,
      requiresSlot,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Name"
        placeholder="e.g. Full Bridal Commission"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextArea
        label="Description"
        placeholder="What this package includes"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <CheckboxMultiSelect
        label="Bundled services"
        options={options}
        selected={serviceIds}
        onChange={setServiceIds}
        emptyLabel="Create services first, then bundle them here."
      />
      <Input
        label="Bundle price (₦) — 0 for free"
        type="number"
        min={0}
        step="0.01"
        placeholder="0.00"
        value={priceMajor}
        onChange={(e) => setPriceMajor(e.target.value)}
      />
      <DepositRuleFields value={depositRule} onChange={setDepositRule} />
      <div className="pt-1">
        <Toggle
          label="Requires a calendar slot (off = delivered async, provider schedules sessions later)"
          checked={requiresSlot}
          onChange={setRequiresSlot}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving} loading={isSaving}>
          {initial ? 'Save changes' : 'Create package'}
        </Button>
      </div>
    </form>
  );
}
